/**
 * Profile.jsx — Read-only profile page.
 * Access: http://localhost:3000/my-account-profile-mv
 *
 * Styling follows the project convention (Tailwind + Ant Design, blue theme
 * matching Dashboard.jsx bg-[#f0f5ff] and rounded-xl shadow patterns).
 */
import React, { useEffect, useState } from "react";
import { Card, Spin, Alert, Tag, Avatar, Progress } from "antd";
import {
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  BankOutlined,
  IdcardOutlined,
  CrownOutlined,
  CalendarOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  SafetyCertificateOutlined,
  TeamOutlined,
  FileTextOutlined,
  ThunderboltOutlined,
  TrophyOutlined,
} from "@ant-design/icons";
import { fetchData } from "../api/apiService";
import { USERS } from "../api/endpointMap";
import moment from "moment";

const Profile = () => {
  const [user, setUser] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [userResp, statusResp] = await Promise.all([
          fetchData(USERS.USER_INFO),
          fetchData("/api/v1/subscriptions/status").catch(() => null),
        ]);
        setUser(userResp || {});
        setStatus(statusResp?.result || statusResp?.data || statusResp || {});
      } catch (err) {
        console.error("Profile fetch error:", err);
        setError(err?.message || "Failed to load profile");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] bg-[#f0f5ff]">
        <Spin size="large" tip="Loading profile..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-10 max-w-3xl mx-auto bg-[#f0f5ff] min-h-screen">
        <Alert message="Failed to load profile" description={error} type="error" showIcon />
      </div>
    );
  }

  const org = user?.organization || {};
  const initials = (user?.full_name || user?.email || "U")
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

  // Subscription state from /subscriptions/status (authoritative source)
  const isTrial = status?.is_trial ?? org?.is_trial;
  const isActive = status?.is_active ?? true;
  const subState = status?.subscription_state || (isTrial ? "trial" : "subscribed");
  const daysRemaining = status?.days_remaining ?? null;

  const STATE_TAG = {
    trial: { color: "orange", label: "FREE TRIAL", icon: <ThunderboltOutlined /> },
    trial_expired: { color: "red", label: "TRIAL EXPIRED", icon: <CloseCircleFilled /> },
    subscribed: { color: "green", label: "SUBSCRIBED", icon: <CheckCircleFilled /> },
    expired: { color: "red", label: "EXPIRED", icon: <CloseCircleFilled /> },
  };
  const stateTag = STATE_TAG[subState] || STATE_TAG.trial;

  // Index status.portals by portal name for quick lookup
  const statusByPortal = (status?.portals || []).reduce((acc, p) => {
    acc[p.portal] = p;
    return acc;
  }, {});

  const Field = ({ icon, label, value }) => (
    <div className="flex py-2.5 border-b border-gray-100 last:border-b-0">
      <div className="w-2/5 text-gray-500 text-sm flex items-center gap-2">
        {icon}
        <span>{label}</span>
      </div>
      <div className="flex-1 text-gray-800 font-medium text-sm break-all">
        {value || <span className="text-gray-400">-</span>}
      </div>
    </div>
  );

  const YesNoTag = ({ value }) =>
    value ? (
      <Tag icon={<CheckCircleFilled />} color="success">Yes</Tag>
    ) : (
      <Tag icon={<CloseCircleFilled />} color="default">No</Tag>
    );

  const THEME = {
    "Income Tax": {
      color: "blue",
      textClass: "text-blue-600",
      bgClass: "bg-blue-50",
      borderClass: "border-blue-200",
      strokeColor: "#1890ff",
    },
    GST: {
      color: "green",
      textClass: "text-green-600",
      bgClass: "bg-green-50",
      borderClass: "border-green-200",
      strokeColor: "#52c41a",
    },
    TDS: {
      color: "orange",
      textClass: "text-orange-600",
      bgClass: "bg-orange-50",
      borderClass: "border-orange-200",
      strokeColor: "#fa8c16",
    },
  };

  const portals = ["Income Tax", "GST", "TDS"].map((name) => {
    const s = statusByPortal[name] || {};
    const t = THEME[name];
    return {
      name,
      ...t,
      // `enabled` is true when the portal appears in status.portals (backend
      // only includes enabled portals). Falls back to false when absent.
      // Legal-response separate cap merged into responses on
      // 19 May 2026 — single pool. legalUsed/legalTotal kept at 0
      // for downstream callers that still read them; the meter
      // tile is hidden below.
      enabled: !!statusByPortal[name],
      // Per-portal client COUNT (informational breakdown). The enforced
      // cap is now org-wide — see the `usage` block below.
      used: s.used_clients ?? 0,
    };
  });

  // Org-wide unified usage (1 Jul 2026). Overall Clients + Response
  // Generation are now ONE cap each across all three portals. The
  // per-portal `portals` above stay for the enabled-flag breakdown, but
  // the enforced meters read this block.
  const usage = status?.usage || {};
  const orgClientsUsed = usage.used_clients ?? 0;
  const orgClientsMax = usage.max_clients ?? 0;
  const orgRespUsed = usage.used_responses ?? 0;
  const orgRespMax = usage.max_responses ?? 0;

  return (
    <div className="bg-[#f0f5ff] min-h-screen p-4 md:p-6">
      <div className="max-w-6xl mx-auto">
        {/* Hero Header */}
        <div className="bg-gradient-to-br from-blue-600 via-blue-500 to-indigo-600 rounded-xl shadow-lg p-6 md:p-8 mb-6 text-white">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <Avatar
              size={88}
              className="bg-white/20 border-[3px] border-white/30 text-white flex-shrink-0"
              style={{ fontSize: "32px", fontWeight: 600 }}
            >
              {initials}
            </Avatar>

            <div className="flex-1">
              <h1 className="text-2xl md:text-3xl font-bold m-0 text-white">
                {user?.full_name || "-"}
              </h1>
              <div className="opacity-90 mt-1 text-sm flex items-center gap-1.5">
                <MailOutlined />
                {user?.email || "-"}
              </div>
              <div className="mt-3 flex gap-2 flex-wrap">
                {user?.role && (
                  <Tag icon={<CrownOutlined />} color="gold">
                    {user.role.toUpperCase()}
                  </Tag>
                )}
                {user?.enabled && <Tag color="green">ACTIVE</Tag>}
                <Tag icon={stateTag.icon} color={stateTag.color}>
                  {stateTag.label}
                </Tag>
                {status?.subscription_plan && (
                  <Tag color="geekblue">{status.subscription_plan}</Tag>
                )}
              </div>
            </div>

            {daysRemaining !== null && (
              <div className="text-center md:text-right md:pl-4 md:border-l md:border-white/20">
                <div className="text-xs opacity-85">
                  {isActive
                    ? isTrial
                      ? "Trial Ends In"
                      : "Plan Expires In"
                    : "Status"}
                </div>
                <div className="text-4xl md:text-5xl font-bold leading-none mt-1">
                  {isActive ? daysRemaining : "-"}
                </div>
                <div className="text-xs opacity-85">
                  {isActive ? "days" : subState.replace("_", " ")}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {/* Per-portal CLIENT COUNTS — informational breakdown. The
              enforced cap is now org-wide (see Overall Clients below),
              so these tiles show the count without a per-portal limit. */}
          {portals.map((p) => (
            <div key={p.name} className="bg-white rounded-xl shadow-sm p-4">
              <div className="text-xs text-gray-500 mb-1">{p.name} Clients</div>
              <div className={`text-2xl font-bold ${p.textClass}`}>
                {p.used}
              </div>
              <Tag color={p.enabled ? p.color : "default"} className="mt-2">
                {p.enabled ? "Enabled" : "Disabled"}
              </Tag>
            </div>
          ))}
          <div className="bg-white rounded-xl shadow-sm p-4">
            <div className="text-xs text-gray-500 mb-1">Subscription</div>
            <div className="text-2xl font-bold text-indigo-600">
              {status?.subscription_plan || "-"}
            </div>
            <Tag color={stateTag.color} className="mt-2">
              {stateTag.label}
            </Tag>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
          {/* Personal Details */}
          <Card
            bordered={false}
            className="rounded-xl shadow-sm"
            title={
              <span className="text-base font-semibold">
                <UserOutlined className="text-blue-500 mr-2" />
                Personal Details
              </span>
            }
          >
            <Field icon={<IdcardOutlined />} label="User ID" value={user?.id} />
            <Field icon={<UserOutlined />} label="First Name" value={user?.first_name} />
            <Field icon={<UserOutlined />} label="Last Name" value={user?.last_name} />
            <Field icon={<MailOutlined />} label="Email" value={user?.email} />
            <Field icon={<PhoneOutlined />} label="Phone" value={user?.phone_number} />
            <Field
              icon={<CrownOutlined />}
              label="Role"
              value={user?.role ? <Tag color="blue">{user.role}</Tag> : null}
            />
            <Field
              icon={<CheckCircleFilled />}
              label="Account Enabled"
              value={<YesNoTag value={user?.enabled} />}
            />
            <Field
              icon={<SafetyCertificateOutlined />}
              label="Read-Only"
              value={
                user?.read_only ? (
                  <Tag color="orange">Yes</Tag>
                ) : (
                  <Tag color="default">No</Tag>
                )
              }
            />
            <Field
              icon={<CalendarOutlined />}
              label="Last Login"
              value={
                user?.last_login
                  ? moment.utc(user.last_login).local().format("MMM Do YYYY, h:mm a")
                  : null
              }
            />
            <Field icon={<UserOutlined />} label="Referred" value={user?.referred} />
          </Card>

          {/* Address & Billing */}
          <Card
            bordered={false}
            className="rounded-xl shadow-sm"
            title={
              <span className="text-base font-semibold">
                <EnvironmentOutlined className="text-green-500 mr-2" />
                Address & Billing
              </span>
            }
          >
            <Field icon={<EnvironmentOutlined />} label="Address" value={user?.address} />
            <Field icon={<EnvironmentOutlined />} label="City" value={user?.city} />
            <Field icon={<EnvironmentOutlined />} label="State" value={user?.state} />
            <Field icon={<EnvironmentOutlined />} label="Pincode" value={user?.pincode} />
            <Field icon={<IdcardOutlined />} label="GST Number" value={user?.gst_number} />
            <Field
              icon={<TrophyOutlined />}
              label="Is First Purchase"
              value={<YesNoTag value={user?.is_first_purchase} />}
            />
            <Field
              icon={<CheckCircleFilled />}
              label="Subscription Status"
              value={
                user?.subscription_status ? (
                  <Tag color="cyan">{user.subscription_status}</Tag>
                ) : null
              }
            />
          </Card>
        </div>

        {/* Organization */}
        <Card
          bordered={false}
          className="rounded-xl shadow-sm mb-4"
          title={
            <span className="text-base font-semibold">
              <BankOutlined className="text-purple-500 mr-2" />
              Organization Details
            </span>
          }
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
            <div>
              <Field icon={<IdcardOutlined />} label="Organization ID" value={org?.id} />
              <Field icon={<BankOutlined />} label="Company Name" value={org?.company_name} />
              <Field icon={<IdcardOutlined />} label="Org Reference" value={org?.org_reference} />
              <Field
                icon={<ThunderboltOutlined />}
                label="Trial"
                value={<YesNoTag value={org?.is_trial} />}
              />
            </div>
            <div>
              <Field
                icon={<CrownOutlined />}
                label="Plan"
                value={
                  org?.subscription_plan ? (
                    <Tag color="gold">{org.subscription_plan}</Tag>
                  ) : null
                }
              />
              <Field
                icon={<TrophyOutlined />}
                label="Category"
                value={org?.subscription_category}
              />
              <Field
                icon={<CalendarOutlined />}
                label="Trial Period"
                value={
                  org?.trial_start_date && org?.trial_end_date
                    ? `${moment(org.trial_start_date).format("MMM D, YYYY")} → ${moment(
                        org.trial_end_date
                      ).format("MMM D, YYYY")}`
                    : null
                }
              />
              <Field
                icon={<CalendarOutlined />}
                label="Subscription Period"
                value={
                  org?.subscription_start_date && org?.subscription_end_date
                    ? `${moment(org.subscription_start_date).format(
                        "MMM D, YYYY"
                      )} → ${moment(org.subscription_end_date).format("MMM D, YYYY")}`
                    : null
                }
              />
            </div>
          </div>
        </Card>

        {/* Overall Usage — org-wide unified quotas (1 Jul 2026). Overall
            Clients and Response Generation are ONE limit each, spanning
            Income Tax, GST and TDS. */}
        <Card
          bordered={false}
          className="rounded-xl shadow-sm"
          title={
            <span className="text-base font-semibold">
              <TeamOutlined className="text-orange-500 mr-2" />
              Overall Usage
            </span>
          }
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              {
                key: "clients",
                label: "Overall Clients",
                sub: "Across Income Tax, GST and TDS",
                icon: <TeamOutlined className="mr-1" />,
                used: orgClientsUsed,
                total: orgClientsMax,
              },
              {
                key: "responses",
                label: "Response Generation",
                sub: "Across all portals (AI and legal share this pool)",
                icon: <ThunderboltOutlined className="mr-1" />,
                used: orgRespUsed,
                total: orgRespMax,
              },
            ].map((m) => {
              const remaining = Math.max(0, m.total - m.used);
              const pct = m.total > 0 ? Math.round((m.used / m.total) * 100) : 0;
              const isFull = m.total > 0 && m.used >= m.total;
              const isLow = !isFull && m.total > 0 && remaining <= 5;
              const stroke = isFull ? "#ef4444" : isLow ? "#f59e0b" : "#2563eb";
              return (
                <div
                  key={m.key}
                  className="p-5 rounded-xl border bg-blue-50 border-blue-100"
                >
                  <div className="flex justify-between items-center mb-3.5">
                    <div className="font-bold text-base text-blue-700">
                      {m.icon}
                      {m.label}
                    </div>
                    <div className="flex gap-1">
                      {isFull && <Tag color="red">FULL</Tag>}
                      {isLow && <Tag color="orange">LOW</Tag>}
                    </div>
                  </div>
                  <div className="text-xs text-gray-500 mb-2">{m.sub}</div>
                  <div className="flex justify-between mb-1 text-xs">
                    <span className="text-gray-600">Used</span>
                    <span className="text-blue-700 font-semibold">
                      {m.used} / {m.total}
                    </span>
                  </div>
                  <Progress
                    percent={pct}
                    strokeColor={stroke}
                    showInfo={false}
                    size="small"
                  />
                  <div className="flex justify-between mt-1 text-[11px]">
                    <span className="text-gray-500">{pct}% used</span>
                    <span className={`font-semibold ${isFull ? "text-red-500" : "text-green-600"}`}>
                      {remaining} pending
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Profile;